import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { useTranslation } from "react-i18next";
import { INVALID_URL, ItemResultType, ResultType } from "@app/constants";
import { IconInvalidUrl, IconPlay } from "@app/components/icons";
import { IconDeleteRed } from "@app/components/icons/IconDeleteRed/IconDeleteRed";
import { IconWarningFormEdit } from "@app/components/icons";
import {
  IconMaintenaceNew,
  IconNotApplicableDashInspection,
} from "@app/components/icons/IconMaintenace/IconMaintenanceNew";
import { IconSelectedNew } from "@app/components/icons/IconSelected/IconSelectedNew";
import { REGEX } from "@app/helpers";
import { MediaUpload } from "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/MediaUpload/MediaUpload";
import type {
  ItemDraft,
  TemplateInspectionItem,
  TemplateInspectionItemMedia,
} from "./createInspectionForm.types";
import {
  INSPECTION_MEDIA_TOTAL_LIMIT,
  getNumericFieldLabel,
  getNumericMax,
  getNumericUnitSuffix,
  getSortedPredefinedOptions,
  hasPredefinedOptions,
  isIconResultItem,
  isNumericValueValid,
  normalizeNumericOnBlur,
  sanitizeNumericInputValue,
} from "./createInspectionForm.utils";

const HistoryIconButton = memo(function HistoryIconButton({
  visible,
  isAbnormalPrevious = false,
  onClick,
}: {
  visible: boolean;
  isAbnormalPrevious?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <Tooltip title={t("createInspection.viewPrevious")}>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2px",
          flexShrink: 0,
          minWidth: 32,
          height: 28,
          px: isAbnormalPrevious ? "6px" : "4px",
          border: "1px solid #d0d0d0",
          borderRadius: "8px",
          backgroundColor: "#fff",
          cursor: "pointer",
          color: "#9e9e9e",
          padding: isAbnormalPrevious ? "2px 6px" : "2px 4px",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "#757575",
          },
        }}
      >
        <HistoryOutlinedIcon
          sx={{
            fontSize: 18,
            color: isAbnormalPrevious ? "#DA9309" : "inherit",
          }}
        />
        {isAbnormalPrevious && (
          <PriorityHighIcon
            sx={{
              fontSize: 16,
              color: "#DA9309",
              ml: "-2px",
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
});

function isLikelyVideoPath(url: string): boolean {
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp4|mov|webm|m4v|avi)$/i.test(path);
}

function isTemplateMediaVideo(media: TemplateInspectionItemMedia): boolean {
  const mime = (media.mimeType || "").toLowerCase();
  if (mime.includes("video")) return true;
  return (
    isLikelyVideoPath(media.mediaUrl || "") ||
    isLikelyVideoPath(media.fileName || "")
  );
}

function getTemplateMediaPreviewUrl(
  media: TemplateInspectionItemMedia,
): string | undefined {
  const url = media.mediaUrl?.trim();
  if (!url || url === INVALID_URL) return undefined;
  return url;
}

function getTemplateMediaThumbnailUrl(
  media: TemplateInspectionItemMedia,
): string | undefined {
  if (isTemplateMediaVideo(media)) {
    const thumb = media.thumbnailUrl?.trim();
    if (thumb && thumb !== INVALID_URL) return thumb;
    return undefined;
  }
  return getTemplateMediaPreviewUrl(media);
}

const templateMediaThumbBoxSx = {
  width: 80,
  height: 80,
  borderRadius: 1,
  overflow: "hidden",
  cursor: "pointer",
  border: "1px solid #ddd",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "#f5f5f5",
  position: "relative",
} as const;

const TemplateItemMediaThumb = memo(function TemplateItemMediaThumb({
  media,
  onPreview,
}: {
  media: TemplateInspectionItemMedia;
  onPreview: (media: TemplateInspectionItemMedia) => void;
}) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const isVideo = isTemplateMediaVideo(media);
  const previewUrl = getTemplateMediaPreviewUrl(media);
  const thumbUrl = getTemplateMediaThumbnailUrl(media);
  const canPreview = !!previewUrl;

  return (
    <Box
      onClick={() => canPreview && onPreview(media)}
      sx={{
        ...templateMediaThumbBoxSx,
        cursor: canPreview ? "pointer" : "default",
        bgcolor: isVideo ? "#000" : "#f5f5f5",
      }}
    >
      {thumbUrl && !thumbFailed ? (
        <Box
          component="img"
          className="swiper-image"
          src={thumbUrl}
          alt={media.fileName}
          onError={() => setThumbFailed(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isVideo && canPreview ? <IconPlay /> : <IconInvalidUrl />}
        </Box>
      )}
      {isVideo && canPreview && thumbUrl && !thumbFailed && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "rgba(0,0,0,0.45)",
            borderRadius: "50%",
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <IconPlay />
        </Box>
      )}
    </Box>
  );
});

const ItemAudioPlayerLayout = memo(function ItemAudioPlayerLayout({
  src,
  audioRef,
  onRemove,
}: {
  src: string;
  audioRef?: React.RefObject<HTMLAudioElement>;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ marginTop: "12px", touchAction: "auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <audio ref={audioRef} controls src={src} style={{ flex: 1 }} />
        <Tooltip title={t("formAction.delete")}>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: "#d32f2f",
              marginLeft: 2,
              borderRadius: "50%",
              transition: "all 0.3s ease",
              "&:hover": {
                border: "2px solid #d32f2f",
                backgroundColor: "transparent",
              },
            }}
          >
            <IconDeleteRed />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
});

const ItemBlobAudio = memo(function ItemBlobAudio({
  blob,
  onRemove,
}: {
  blob: Blob;
  onRemove: () => void;
}) {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState("");

  useEffect(() => {
    if (!blob?.size) {
      setSrc("");
      return;
    }
    const url = URL.createObjectURL(blob);
    setSrc(url);
    const el = audioRef.current;
    if (el) {
      el.src = url;
      el.load();
    }
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (!src) return null;

  return (
    <ItemAudioPlayerLayout src={src} audioRef={audioRef} onRemove={onRemove} />
  );
});

const ItemUrlAudio = memo(function ItemUrlAudio({
  url,
  onRemove,
}: {
  url: string;
  onRemove: () => void;
}) {
  if (!url) return null;

  return <ItemAudioPlayerLayout src={url} onRemove={onRemove} />;
});

const RecordingTimerBadge = memo(function RecordingTimerBadge({
  active,
  paused,
  startTimeMs,
}: {
  active: boolean;
  paused: boolean;
  startTimeMs: number | null;
}) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active || !startTimeMs) {
      setElapsed(0);
      return;
    }

    const tick = () => {
      setElapsed(Math.min(Math.floor((Date.now() - startTimeMs) / 1000), 60));
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [active, startTimeMs]);

  if (!active || elapsed >= 60) return null;

  if (paused) {
    return (
      <Tooltip title={t("paused")} arrow>
        <div
          className="glow-yellow"
          style={{
            marginTop: "4px",
            fontSize: "12px",
            color: "#f9a825",
            backgroundColor: "#fff8e1",
            padding: "4px 8px",
            borderRadius: "4px",
            boxShadow: "0 0 8px #fbc02d",
            fontWeight: "bold",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            height: "32px",
            width: "60px",
          }}
        >
          <PauseCircleIcon fontSize="small" />
          {`${elapsed}s`}
        </div>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={t("recording")} arrow>
      <div
        className="glow-red"
        style={{
          marginTop: "4px",
          fontSize: "12px",
          color: "#d32f2f",
          backgroundColor: "#ffe6e6",
          padding: "4px 8px",
          borderRadius: "4px",
          boxShadow: "0 0 8px #f44336",
          fontWeight: "bold",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: "32px",
          width: "60px",
        }}
      >
        <FiberManualRecordIcon fontSize="small" />
        <span style={{ transform: "translateY(1px)" }}>{`${elapsed}s`}</span>
      </div>
    </Tooltip>
  );
});

const ResultIconControls = memo(function ResultIconControls({
  itemId,
  resultValue,
  showOkAnomaly,
  showNotApplicable,
  onPick,
}: {
  itemId: string;
  resultValue: string;
  showOkAnomaly: boolean;
  showNotApplicable: boolean;
  onPick: (itemId: string, value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      className="radio-group-container"
      sx={{
        flexShrink: 0,
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: "20px",
        overflowX: "auto",
      }}
    >
      {showNotApplicable && (
        <Tooltip title={t("notapplicable")} arrow>
          <Box
            onClick={() =>
              onPick(
                itemId,
                resultValue === ItemResultType.NOT_APPLICABLE
                  ? ""
                  : ItemResultType.NOT_APPLICABLE,
              )
            }
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <IconNotApplicableDashInspection
              fillColor={
                resultValue === ItemResultType.NOT_APPLICABLE
                  ? "#696969"
                  : "#D3D3D3"
              }
              dashColor="white"
              size={resultValue === ItemResultType.NOT_APPLICABLE ? 44 : 37}
            />
          </Box>
        </Tooltip>
      )}
      {showOkAnomaly && (
        <>
          <Tooltip title={t("abnormal")} arrow>
            <Box
              onClick={() =>
                onPick(
                  itemId,
                  resultValue === ItemResultType.ANOMARY
                    ? ""
                    : ItemResultType.ANOMARY,
                )
              }
              sx={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <IconMaintenaceNew
                fillColor={
                  resultValue === ItemResultType.ANOMARY ? "#DA9309" : "#FFF8C5"
                }
                iconColor={
                  resultValue === ItemResultType.ANOMARY ? "white" : "#DA9309"
                }
              />
            </Box>
          </Tooltip>
          <Tooltip title={t("normal")} arrow>
            <Box
              onClick={() =>
                onPick(
                  itemId,
                  resultValue === ItemResultType.OK ? "" : ItemResultType.OK,
                )
              }
              sx={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <IconSelectedNew
                fillColor={
                  resultValue === ItemResultType.OK ? "#0e7c47" : "#DAFBE1"
                }
                iconColor={
                  resultValue === ItemResultType.OK ? "white" : "#1A7F37"
                }
              />
            </Box>
          </Tooltip>
        </>
      )}
    </Box>
  );
});

type NonNumericItemRowProps = {
  item: TemplateInspectionItem;
  itemIndex: number;
  draft: ItemDraft;
  validationAttempted: boolean;
  mediaAvailable: boolean;
  isRecording: boolean;
  isPaused: boolean;
  recordingForItemId: string | null;
  recordingStartTimeMs: number | null;
  totalInspectionMediaCount: number;
  mediaPreviewNonce: number;
  uploadDescription: string[];
  onPatchDraft: (itemId: string, patch: Partial<ItemDraft>) => void;
  onToggleCommentSection: (itemId: string) => void;
  onResultIconPick: (itemId: string, value: string) => void;
  onStartRecording: (itemId: string) => void;
  onStopRecording: () => void;
  onRequestDeleteRecording: (itemId: string) => void;
  onPreviewTemplateMedia: (media: TemplateInspectionItemMedia) => void;
  onPreviewUploadMedia: (url: string | string[]) => void;
  onMediaFilesChange: (itemId: string, files: File[]) => void;
  onFileCountChange: (
    itemId: string,
    count: number | ((prev: number) => number),
  ) => void;
  onUploadErrorChange: (itemId: string, hasError: boolean) => void;
  setItemRef: (itemId: string, el: HTMLDivElement | null) => void;
  hasError?: boolean;
  hasPreviousResult: boolean;
  isAbnormalPreviousResult?: boolean;
  onOpenPreviousResult: (itemId: string) => void;
};

export const InspectionNonNumericItemRow = memo(
  function InspectionNonNumericItemRow({
    item,
    itemIndex,
    draft,
    validationAttempted,
    mediaAvailable,
    isRecording,
    isPaused,
    recordingForItemId,
    recordingStartTimeMs,
    totalInspectionMediaCount,
    mediaPreviewNonce,
    uploadDescription,
    onPatchDraft,
    onToggleCommentSection,
    onResultIconPick,
    onStartRecording,
    onStopRecording,
    onRequestDeleteRecording,
    onPreviewTemplateMedia,
    onPreviewUploadMedia,
    onMediaFilesChange,
    onFileCountChange,
    onUploadErrorChange,
    setItemRef,
    hasError = false,
    hasPreviousResult,
    isAbnormalPreviousResult = false,
    onOpenPreviousResult,
  }: NonNumericItemRowProps) {
    const { t } = useTranslation();

    const showOkAnomaly =
      item.resultType === ResultType.OK_OR_ANOMARY ||
      item.resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE;
    const showNotApplicable =
      item.resultType === ResultType.OK_ANOMARY_NOT_APPLICABLE;
    const hasResultIcons = isIconResultItem(item);
    const hasFreeTextOptions =
      item.resultType === ResultType.FREE_TEXT && hasPredefinedOptions(item);
    const predefinedOptions = useMemo(
      () => (hasFreeTextOptions ? getSortedPredefinedOptions(item) : []),
      [hasFreeTextOptions, item],
    );
    const hasSampleMedias = (item.inspectionItemMedias?.length ?? 0) > 0;
    const showMediaIconsRow = hasSampleMedias || hasResultIcons;
    const thisItemRecording = recordingForItemId === item.inspectionItemId;
    const recordStartDisabled =
      !mediaAvailable ||
      (isRecording && !thisItemRecording) ||
      isPaused ||
      !!draft.recordedBlob ||
      !!draft.existingRecordingUrl;
    const showWhitespaceCommentError =
      validationAttempted &&
      (draft.resultValue === ItemResultType.OK ||
        draft.resultValue === ItemResultType.NOT_APPLICABLE) &&
      draft.comment !== "" &&
      !draft.comment.trim();

    const isAbnormalSelected = draft.resultValue === ItemResultType.ANOMARY;
    const hasEvidence =
      draft.comment.trim().length > 0 ||
      draft.mediaFiles.length > 0 ||
      draft.fileCount > 0 ||
      !!draft.recordedBlob ||
      !!draft.existingRecordingUrl;
    const showEvidenceRequiredError =
      hasResultIcons &&
      validationAttempted &&
      isAbnormalSelected &&
      !hasEvidence;
    const showFreeTextRequiredError =
      hasFreeTextOptions &&
      validationAttempted &&
      !draft.selectedFreeText.trim();
    const addCommentFileColor = isAbnormalSelected
      ? showEvidenceRequiredError
        ? "#d32f2f"
        : "#0e7c47"
      : hasFreeTextOptions
        ? showFreeTextRequiredError
          ? "#d32f2f"
          : draft.selectedFreeText.trim()
            ? "#0e7c47"
            : "#696969"
        : "#696969";

    const handleCommentChange = useCallback(
      (value: string) => {
        const isInvalid =
          !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
          !REGEX.emojiRegex.test(value);
        onPatchDraft(item.inspectionItemId, {
          comment: value,
          commentError: isInvalid,
        });
      },
      [item.inspectionItemId, onPatchDraft],
    );

    const handleSetFileCount = useCallback(
      (n: number | ((prev: number) => number)) => {
        onFileCountChange(item.inspectionItemId, n);
      },
      [item.inspectionItemId, onFileCountChange],
    );

    const handleSetUploadError = useCallback(
      (err: boolean) => {
        onUploadErrorChange(item.inspectionItemId, err);
      },
      [item.inspectionItemId, onUploadErrorChange],
    );

    const handleSetMedias = useCallback(
      (files: File[]) => {
        onMediaFilesChange(item.inspectionItemId, files);
      },
      [item.inspectionItemId, onMediaFilesChange],
    );

    const fileUploadLimit = totalInspectionMediaCount - draft.mediaFiles.length;
    const mediaLimitExceeded =
      totalInspectionMediaCount > INSPECTION_MEDIA_TOTAL_LIMIT;

    return (
      <Box
        ref={(el: HTMLDivElement | null) =>
          setItemRef(item.inspectionItemId, el)
        }
        sx={{
          pt: itemIndex > 0 ? 2 : 0,
          mt: itemIndex > 0 ? 2 : 0,
          borderTop: itemIndex > 0 ? "1px solid #e0e0e0" : "none",
          ...(hasError && {
            bgcolor: "#fdecea",
            borderRadius: 1,
            mx: -1.5,
            px: 1.5,
            pt: 1.5,
            pb: 1.5,
          }),
        }}
      >
        {!hasSampleMedias && hasResultIcons ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {item.isUpdated && (
                  <Typography
                    variant="caption"
                    className="txt-gray-1"
                    sx={{
                      display: "block",
                      fontWeight: 500,
                      mb: 0.5,
                      ml: "-2px",
                    }}
                  >
                    {t("dialogReport.edited")}
                  </Typography>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "common.black", mb: 0.5 }}
                >
                  {item.name}
                </Typography>
                {!!item.description?.trim() && (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 1.5 }}
                  >
                    {item.description}
                  </Typography>
                )}
              </Box>
              <HistoryIconButton
                visible={hasPreviousResult}
                isAbnormalPrevious={isAbnormalPreviousResult}
                onClick={() => onOpenPreviousResult(item.inspectionItemId)}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 1,
                minHeight: 90,
              }}
            >
              <ResultIconControls
                itemId={item.inspectionItemId}
                resultValue={draft.resultValue}
                showOkAnomaly={showOkAnomaly}
                showNotApplicable={showNotApplicable}
                onPick={onResultIconPick}
              />
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {item.isUpdated && (
                  <Typography
                    variant="caption"
                    className="txt-gray-1"
                    sx={{
                      display: "block",
                      fontWeight: 500,
                      mb: 0.5,
                      ml: "-2px",
                    }}
                  >
                    {t("dialogReport.edited")}
                  </Typography>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "common.black", mb: 0.5 }}
                >
                  {item.name}
                </Typography>
              </Box>
              <HistoryIconButton
                visible={hasPreviousResult}
                isAbnormalPrevious={isAbnormalPreviousResult}
                onClick={() => onOpenPreviousResult(item.inspectionItemId)}
              />
            </Box>
            {!!item.description?.trim() && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 1.5 }}
              >
                {item.description}
              </Typography>
            )}
          </>
        )}

        {hasSampleMedias && showMediaIconsRow && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
              width: "100%",
              minHeight: 90,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 1,
                alignItems: "center",
                py: 0.5,
              }}
            >
              {item.inspectionItemMedias.map((m) => (
                <TemplateItemMediaThumb
                  key={m.inspectionItemMediaId}
                  media={m}
                  onPreview={onPreviewTemplateMedia}
                />
              ))}
            </Box>
            <ResultIconControls
              itemId={item.inspectionItemId}
              resultValue={draft.resultValue}
              showOkAnomaly={showOkAnomaly}
              showNotApplicable={showNotApplicable}
              onPick={onResultIconPick}
            />
          </Box>
        )}

        <Button
          variant="text"
          fullWidth
          onClick={() => onToggleCommentSection(item.inspectionItemId)}
          startIcon={
            draft.commentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
          }
          sx={{
            mt: hasSampleMedias ? 2 : 0.5,
            py: 1,
            px: 1,
            textTransform: "none",
            fontWeight: 600,
            justifyContent: "center",
            color: addCommentFileColor,
            transition:
              "background-color 0.25s ease, box-shadow 0.25s ease, color 0.2s ease",
            "&:hover": { backgroundColor: "action.hover" },
            "& .MuiButton-startIcon": {
              marginRight: "8px",
              marginLeft: 0,
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            },
          }}
        >
          {t("addcommentfile")}
        </Button>

        <Collapse
          in={draft.commentExpanded}
          timeout={{ enter: 420, exit: 280 }}
          easing={{
            enter: "cubic-bezier(0.4, 0, 0.2, 1)",
            exit: "cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <Fade
            in={draft.commentExpanded}
            timeout={{ enter: 360, exit: 200 }}
            easing={{
              enter: "cubic-bezier(0.4, 0, 0.2, 1)",
              exit: "cubic-bezier(0.4, 0, 1, 1)",
            }}
          >
            <Box sx={{ mt: 2 }}>
              <div
                className="wrapper-text-field mt-8"
                style={{ position: "relative" }}
              >
                {!hasFreeTextOptions && (
                  <TextField
                    multiline
                    rows={3}
                    className="input-field"
                    label={t("dialogReport.comment")}
                    value={draft.comment}
                    fullWidth
                    disabled={item.resultType === ResultType.FREE_TEXT}
                    inputProps={{ maxLength: 1000 }}
                    onChange={(e) => handleCommentChange(e.target.value)}
                    error={draft.commentError || showWhitespaceCommentError}
                    helperText={
                      draft.commentError ? t("invalidCharacters") : ""
                    }
                    InputProps={{ style: { paddingRight: "65px" } }}
                  />
                )}

                {hasFreeTextOptions &&
                  (() => {
                    const showFreeTextError =
                      validationAttempted && !draft.selectedFreeText.trim();

                    return (
                      <FormControl
                        fullWidth
                        sx={{ mt: 2 }}
                        error={showFreeTextError}
                      >
                        <InputLabel
                          id={`free-text-select-${item.inspectionItemId}`}
                        >
                          {t("predefinedCommentSelection")}
                        </InputLabel>
                        <Select
                          labelId={`free-text-select-${item.inspectionItemId}`}
                          value={draft.selectedFreeText}
                          label={t("predefinedCommentSelection")}
                          error={showFreeTextError}
                          onChange={(e) => {
                            const value = e.target.value as string;
                            onPatchDraft(item.inspectionItemId, {
                              selectedFreeText: value,
                              comment: value,
                              commentError: false,
                            });
                          }}
                        >
                          <MenuItem value="">
                            <em>{t("pleaseSelect")}</em>
                          </MenuItem>
                          {predefinedOptions.map((option) => (
                            <MenuItem
                              key={
                                option.customInspectionItemsOptionsId ||
                                option.name
                              }
                              value={option.name}
                            >
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  })()}

                {mediaAvailable &&
                  !draft.recordedBlob &&
                  !draft.existingRecordingUrl &&
                  !hasFreeTextOptions && (
                    <div
                      style={{
                        position: "absolute",
                        right: 12,
                        top: 40,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        zIndex: 10,
                        pointerEvents: "auto",
                      }}
                    >
                      {thisItemRecording && (isRecording || isPaused) ? (
                        <Tooltip title={t("stop")}>
                          <StopIcon
                            onClick={onStopRecording}
                            sx={{
                              color: "#d32f2f",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                color: "#a30000",
                                transform: "scale(1.1)",
                              },
                            }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t("start")}>
                          <FiberManualRecordIcon
                            onClick={
                              recordStartDisabled
                                ? undefined
                                : () => onStartRecording(item.inspectionItemId)
                            }
                            sx={{
                              color: recordStartDisabled ? "#ccc" : "#d32f2f",
                              cursor: recordStartDisabled
                                ? "not-allowed"
                                : "pointer",
                              pointerEvents: recordStartDisabled
                                ? "none"
                                : "auto",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                color: recordStartDisabled ? "#ccc" : "#a30000",
                                transform: recordStartDisabled
                                  ? "none"
                                  : "scale(1.1)",
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>
                  )}
              </div>

              {isRecording && thisItemRecording && !hasFreeTextOptions && (
                <RecordingTimerBadge
                  active
                  paused={false}
                  startTimeMs={recordingStartTimeMs}
                />
              )}

              {isPaused && thisItemRecording && !hasFreeTextOptions && (
                <RecordingTimerBadge
                  active
                  paused
                  startTimeMs={recordingStartTimeMs}
                />
              )}

              {draft.recordedBlob && (
                <ItemBlobAudio
                  blob={draft.recordedBlob}
                  onRemove={() =>
                    onRequestDeleteRecording(item.inspectionItemId)
                  }
                />
              )}

              {!draft.recordedBlob && draft.existingRecordingUrl && (
                <ItemUrlAudio
                  url={draft.existingRecordingUrl}
                  onRemove={() =>
                    onRequestDeleteRecording(item.inspectionItemId)
                  }
                />
              )}

              {thisItemRecording && (isRecording || isPaused) && (
                <div
                  className="warning flex justify-content-start"
                  style={{ marginBottom: 12 }}
                >
                  <IconWarningFormEdit />
                  <span
                    style={{ marginLeft: 12, flex: 1 }}
                    className="txt-black-1"
                  >
                    {t("warningUpdateItem3")}
                  </span>
                </div>
              )}

              <div className="update-media-upload-file mt-24 mb-24 w-100">
                <MediaUpload
                  key={`${item.inspectionItemId}-${mediaPreviewNonce}`}
                  id={item.inspectionItemId}
                  setFileCount={handleSetFileCount}
                  medias={draft.mediaFiles}
                  setHasUploadError={handleSetUploadError}
                  fileUploadLimit={fileUploadLimit}
                  showMaxUploadLimit={true}
                  errMessage={
                    mediaLimitExceeded
                      ? t("maxFilesExceeded", {
                          max: INSPECTION_MEDIA_TOTAL_LIMIT,
                        })
                      : ""
                  }
                  setMedias={handleSetMedias}
                  onPreviewMedia={onPreviewUploadMedia}
                  uploadDescription={uploadDescription}
                  maxFiles={5}
                />
              </div>

              {isAbnormalSelected && (
                <div className="warning flex justify-content-start">
                  <IconWarningFormEdit />
                  <span
                    style={{ marginLeft: 12, flex: 1 }}
                    className="txt-black-1"
                  >
                    {t("createAbnormality.commentOrFileWarning")}
                  </span>
                </div>
              )}
            </Box>
          </Fade>
        </Collapse>
      </Box>
    );
  },
);

type NumericItemRowProps = {
  item: TemplateInspectionItem;
  draft: ItemDraft;
  validationAttempted: boolean;
  onPatchDraft: (itemId: string, patch: Partial<ItemDraft>) => void;
  setItemRef: (itemId: string, el: HTMLDivElement | null) => void;
  hasPreviousResult: boolean;
  isAbnormalPreviousResult?: boolean;
  onOpenPreviousResult: (itemId: string) => void;
};

export const InspectionNumericItemRow = memo(function InspectionNumericItemRow({
  item,
  draft,
  validationAttempted,
  onPatchDraft,
  setItemRef,
  hasPreviousResult,
  isAbnormalPreviousResult = false,
  onOpenPreviousResult,
}: NumericItemRowProps) {
  const { t } = useTranslation();
  const numericMax = getNumericMax(item);
  const numericVal = draft.numericValue.trim();
  const missingRequired = item.isRequired && !numericVal;
  const formatInvalid =
    !!numericVal && !isNumericValueValid(numericVal, numericMax);
  const overMax =
    !!numericVal &&
    Number.isFinite(parseFloat(numericVal)) &&
    parseFloat(numericVal) > numericMax;
  const showFieldValidation = draft.numericTouched || validationAttempted;
  const showRequiredError = showFieldValidation && missingRequired;
  const showNumericError =
    showRequiredError || overMax || (showFieldValidation && formatInvalid);
  const numericHelperText = overMax
    ? t("createInspection.numericMax", { max: numericMax })
    : item.description?.trim() || undefined;

  return (
    <Box
      ref={(el: HTMLDivElement | null) => setItemRef(item.inspectionItemId, el)}
      sx={{ width: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          width: "100%",
        }}
      >
        <TextField
          className={`input-field ${showNumericError ? "error-border" : ""}`}
          label={getNumericFieldLabel(item, t)}
          required={item.isRequired}
          type="text"
          inputMode="decimal"
          fullWidth
          variant="outlined"
          value={draft.numericValue}
          onChange={(e) => {
            const validatedInput = sanitizeNumericInputValue(
              e.target.value,
              draft.numericValue,
            );
            onPatchDraft(item.inspectionItemId, {
              numericValue: validatedInput,
              numericTouched: true,
            });
          }}
          onBlur={() => {
            const normalized = normalizeNumericOnBlur(
              draft.numericValue,
              numericMax,
            );
            onPatchDraft(item.inspectionItemId, {
              numericTouched: true,
              ...(normalized !== draft.numericValue
                ? { numericValue: normalized }
                : {}),
            });
          }}
          error={showNumericError}
          helperText={numericHelperText}
          FormHelperTextProps={{
            className: showNumericError ? "error-text" : undefined,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {getNumericUnitSuffix(item)}
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiOutlinedInput-input": {
              textAlign: "right",
              direction: "ltr",
              paddingRight: "8px",
            },
          }}
          inputProps={{ step: "0.1", min: "0" }}
        />
        {hasPreviousResult && (
          <Box
            sx={{
              pt: 1,
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <HistoryIconButton
              visible={hasPreviousResult}
              isAbnormalPrevious={isAbnormalPreviousResult}
              onClick={() => onOpenPreviousResult(item.inspectionItemId)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
});
